import { useState } from 'react'
import { Box, Paper, Skeleton, Switch, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import SharedButton from '../../shared/components/Button'
import SharedDialog from '../../shared/components/Dialog'
import { useThemeMode } from '../../shared/hooks'
import { notify } from '../../shared/utilities/util'
import type { BaseQueryErrorShape } from '../../store/api/baseQuery'
import { useGetTradesmanSwitchQuery, useSetTradesmanSwitchMutation } from '../../store/api/adminApi'
import { tokens } from '../assistantConfig/styles'

const failureMessage = (error: unknown) =>
  (error as BaseQueryErrorShape)?.message || 'The Tradesman switch could not be changed. Please try again.'

/**
 * SuperAdmin-only card on Subscriber details (nhrapp-admin#65, D42-f). The switch shows the server's value and
 * flips only after the PATCH succeeds. Turning it on is confirmed in a dialog that also shows a refusal; turning
 * it off asks nothing.
 */
const TradesmanSwitchCard = ({ companyId, companyName }: { companyId: string; companyName: string }) => {
  const { isDark } = useThemeMode()
  const t = tokens(isDark)
  const { data, isLoading, isFetching, isError, refetch } = useGetTradesmanSwitchQuery(companyId, {
    refetchOnMountOrArgChange: true,
  })
  const [setTradesmanSwitch, { data: written, isLoading: saving }] = useSetTradesmanSwitchMutation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmError, setConfirmError] = useState<string | null>(null)

  // The write's answer bridges the refetch it triggers, so the switch never flips back while that runs.
  const enabled = (isFetching && written ? written : data)?.crewEnabled === true
  const busy = saving || isFetching

  const turnOff = async () => {
    try {
      await setTradesmanSwitch({ companyId, crewEnabled: false }).unwrap()
      notify('Tradesman is off for crews', 'success')
    } catch (error) {
      notify(failureMessage(error), 'error')
    }
  }

  const turnOn = async () => {
    setConfirmError(null)
    try {
      await setTradesmanSwitch({ companyId, crewEnabled: true }).unwrap()
      setConfirmOpen(false)
      notify('Tradesman is on for crews', 'success')
    } catch (error) {
      setConfirmError(failureMessage(error))
    }
  }

  const onToggle = (next: boolean) => {
    if (busy) return
    if (next) {
      setConfirmError(null)
      setConfirmOpen(true)
    } else {
      void turnOff()
    }
  }

  const tag = (on: boolean) => (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        px: 1,
        py: '2px',
        borderRadius: '999px',
        border: `1px solid ${on ? t.greenBorder : t.border}`,
        bgcolor: on ? t.greenBg : 'background.paper',
        fontSize: '12px',
        fontWeight: 600,
        color: on ? t.green : 'text.secondary',
        '&::before': { content: '""', width: 7, height: 7, borderRadius: '50%', bgcolor: on ? t.green : t.muted },
      }}
    >
      {on ? 'On' : 'Off'}
    </Box>
  )

  const failed = isError || (!isLoading && !data)

  return (
    <Paper
      elevation={0}
      component="section"
      aria-label="Tradesman crew features"
      sx={{ border: `1px solid ${t.border}`, borderRadius: '12px', p: { xs: 2.5, sm: 3 } }}
    >
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          mb: 2,
        }}
      >
        Crew features
      </Typography>

      {isLoading ? (
        <Skeleton variant="rectangular" height={64} sx={{ borderRadius: '10px' }} />
      ) : failed ? (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Typography role="alert" sx={{ fontSize: '13px', color: t.dangerText }}>
            Could not load the Tradesman switch.
          </Typography>
          <SharedButton
            label="Try again"
            btnVariant="secondary"
            size="small"
            loading={isFetching}
            onClick={() => void refetch()}
          />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: { xs: 'wrap', md: 'nowrap' },
              gap: { xs: 1.5, md: 3 },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h2"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1,
                  fontSize: '15px',
                  fontWeight: 700,
                  color: 'text.primary',
                }}
              >
                Tradesman (crew features)
                {tag(enabled)}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: 'text.secondary', mt: 0.75, maxWidth: 520, lineHeight: 1.5 }}>
                Off hides Tradesman from every crew member in this company. The owner preview is not affected.
              </Typography>
            </Box>

            <Box
              component="label"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0,
                width: { xs: '100%', md: 'auto' },
                minHeight: 44,
                pl: 2,
                pr: 1.5,
                borderRadius: '10px',
                border: `1px solid ${t.inputBorder}`,
                bgcolor: 'background.paper',
                fontSize: '13px',
                fontWeight: 600,
                color: 'text.primary',
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.55 : 1,
              }}
            >
              Tradesman
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Switch
                  color="success"
                  checked={enabled}
                  disabled={busy}
                  onChange={(event) => onToggle(event.target.checked)}
                  inputProps={{ role: 'switch', 'aria-label': 'Tradesman for crews on or off' }}
                />
                <Box component="span" sx={{ minWidth: 22 }}>
                  {enabled ? 'On' : 'Off'}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              gap: { xs: 0.5, md: 2 },
              mt: 2,
              pt: 1.5,
              borderTop: `1px solid ${t.grid}`,
              fontSize: '12px',
              color: 'text.secondary',
            }}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
              Changes reach the crew app within 60 seconds.
            </Box>
            {!enabled && <Box component="span">Off by default</Box>}
          </Box>
        </>
      )}

      {confirmOpen && (
        <SharedDialog
          variant="confirm"
          open
          maxWidth={420}
          onClose={() => !saving && setConfirmOpen(false)}
          icon={<PowerSettingsNewIcon sx={{ fontSize: 28, color: t.green }} />}
          iconBg={t.greenBg}
          title="Turn on Tradesman for crews?"
          confirmLabel="Turn on"
          confirmColor="primary"
          confirmLoading={saving}
          focusCancel
          error={confirmError ?? undefined}
          onConfirm={() => !saving && void turnOn()}
        >
          <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.5 }}>
            Every crew member at{' '}
            <Box component="b" sx={{ color: 'text.primary' }}>
              {companyName || 'this company'}
            </Box>{' '}
            will see Tradesman in Piece Work Pro.
          </Typography>
          <Box
            sx={{
              mt: 2,
              width: '100%',
              textAlign: 'left',
              px: 1.75,
              py: 1.25,
              borderRadius: '8px',
              border: `1px solid ${t.border}`,
              bgcolor: t.headBg,
              fontSize: '13px',
              color: 'text.primary',
              lineHeight: 1.6,
            }}
          >
            They will get:
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.25 }}>
              <li>the Tradesman tab and the game card</li>
              <li>Tradesman push notifications</li>
            </Box>
          </Box>
        </SharedDialog>
      )}
    </Paper>
  )
}

export default TradesmanSwitchCard
